import { type Registry as Services, service } from '@ember/service';
import { htmlSafe, type SafeString } from '@ember/template';
import Component from '@glimmer/component';
import type { CustomEmberTableColumn } from '@mylighthouse/prism-ember/types/components/prism/table';
import type { Dayjs } from 'dayjs';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { modifier } from 'ember-modifier';
import { and } from 'ember-truth-helpers';
import { OTB_DEFAULT_VALUE } from 'frontend-utils/utils/constants/constant-values';
import GeneralPercentageChangeValue from 'lumen/components/general/percentage-change-value';
import HotelChannelPricingTooltipsDayCellRoomtypeAvailabilityTooltip from 'lumen/components/hotel/channel-pricing/tooltips/day-cell-room-type-availability-tooltip';
import type { Occupancy, Period, PredictedOccupancy } from 'lumen/models';
import { timeout } from 'lumen/utils/waiters';

import styles from './th-day-table-value-cell.module.css';

interface CustomColumn extends CustomEmberTableColumn {
  date: Dayjs;
  id: string;
  isLoading: boolean;
  period: Period | undefined;
  predictedOccupancyData: PredictedOccupancy;
}

interface HotelChannelPricingTablesCellsDayTableThDayTableValueCellSignature {
  Args: {
    column: CustomColumn;
  };
}

export default class HotelChannelPricingTablesCellsDayTableThDayTableValueCell extends Component<HotelChannelPricingTablesCellsDayTableThDayTableValueCellSignature> {
  @service declare access: Services['access'];
  @service('channel-pricing/table-hover')
  declare channelPricingTableHover: Services['channel-pricing/table-hover'];
  @service declare dates: Services['dates'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare usageAnalytics: Services['usage-analytics'];

  handleHoverEventHandlers = modifier((cellWrappingElement) => {
    const mouseEnterHandler = (): void => {
      this.channelPricingTableHover.hoveredColumnId = this.args.column.id;

      // Added for usage analytics tracking
      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.cellHoverTrackingTask.perform();
    };

    const mouseLeaveHandler = (): void => {
      this.channelPricingTableHover.hoveredColumnId = null;

      // eslint-disable-next-line @typescript-eslint/no-floating-promises
      this.cellHoverTrackingTask.cancelAll();
    };

    cellWrappingElement.addEventListener('mouseenter', mouseEnterHandler);
    cellWrappingElement.addEventListener('mouseleave', mouseLeaveHandler);

    return (): void => {
      cellWrappingElement.removeEventListener('mouseenter', mouseEnterHandler);
      cellWrappingElement.removeEventListener('mouseleave', mouseLeaveHandler);
    };
  });

  cellHoverTrackingTask = task({ drop: true }, async (): Promise<void> => {
    await timeout('extra-long');

    this.usageAnalytics.track('Forecast Previewed', {
      eventTrigger: 'day-bar',
      product: 'smart-distribution',
    });
  });

  get cellId(): string {
    return this.args.column.id;
  }

  get formattedDate(): string {
    return this.args.column.date.format(
      this.dates.partialDateFormat.WEEKDAY_AND_MONTH_AND_DAY_SHORT,
    );
  }

  get formattedOccupancyPercentage(): string {
    return this.occupancyData?.formattedOccupancyPercentage ?? '';
  }

  get isCellOfHoveredColumn(): boolean {
    if (
      this.channelPricingTableHover.hoveredColumnId !== null &&
      this.channelPricingTableHover.hoveredColumnId === this.args.column.id
    ) {
      return true;
    }

    return false;
  }

  get isPastDay(): boolean {
    // Same implementation as in days.isPast function
    return this.hotelAndMarketService.isPast(this.args.column.date);
  }

  get isToday(): boolean {
    return this.args.column.date.isSame(
      this.hotelAndMarketService.getLocalTime(),
      'day',
    );
  }

  get occupancyData(): Occupancy | undefined {
    return this.period?.day?.occupancy;
  }

  get occupancyPercentage(): number | null {
    return this.occupancyData?.occupancyPercentage ?? null;
  }

  // here the bar color is determined by the occupancy percentage
  // the color is determined by the following rules:
  // 0-30% -> general-info-100
  // 31-70% -> general-info-500
  // 71-99% -> general-info-700
  // 100% -> Neutral-alpha-300
  get otbBarColor(): string {
    if (this.occupancyPercentage) {
      if (this.occupancyPercentage < 0.31) {
        return styles['otb-bar-level-1'];
      }
      if (this.occupancyPercentage < 0.71) {
        return styles['otb-bar-level-2'];
      }
      if (this.occupancyPercentage < 1) {
        return styles['otb-bar-level-3'];
      }
      if (this.occupancyPercentage > 0.99) {
        return styles['otb-bar-level-4'];
      }
    }

    return '';
  }

  // https://www.figma.com/design/lpf7w3CxCWeD8tjz2BfjTp/Smart-distribution?node-id=595-30345&t=XpfSVbLqFdylwzGn-0
  // here we use the occupancy percentage to determine the height of the OTB bar
  // the bar's height is rounded to the nearest 10 so increments of 10%
  get otbBarPercentage(): SafeString {
    if (this.occupancyPercentage) {
      let roundedPercentage =
        Math.ceil(Math.ceil(this.occupancyPercentage * 100) / 10) * 10;

      if (roundedPercentage > 100) {
        roundedPercentage = 100;
      }

      return htmlSafe(`height: ${roundedPercentage}%;`);
    }

    return htmlSafe('');
  }

  get period(): Period | undefined {
    return this.args.column.period;
  }

  get showOtbBar(): boolean {
    return Boolean(
      !this.args.column.isLoading &&
      this.access.hasOTB &&
      this.occupancyPercentage &&
      this.occupancyPercentage !== OTB_DEFAULT_VALUE,
    );
  }

  get showTooltip(): boolean {
    return !this.args.column.isLoading && this.access.hasOTB;
  }

  <template>
    <div
      class="{{styles.day-cell}}
        grow p-200
        {{if this.isPastDay styles.is-past}}
        flex h-[52px] items-center justify-end gap-200
        {{if this.isCellOfHoveredColumn styles.highlighted}}"
      data-test-id="day-cell-{{this.cellId}}"
      {{this.handleHoverEventHandlers}}
    >
      <div class="flex flex-col items-end gap-100">
        <p
          class="text-200--semi-bold
            {{if this.isToday 'px-200 py-50'}}
            {{if this.isToday styles.today-date}}"
        >
          {{this.formattedDate}}
        </p>
        {{#if @column.isLoading}}
          <div
            class="table-loading-state-bar mt-100 mb-100 w-full"
            data-test-id="day-cell-loading-bar"
          ></div>
        {{else}}
          <p class="text-200--regular {{styles.otb}}">{{t "rates.OTB"}}
            {{#if this.access.hasOTB}}
              {{this.formattedOccupancyPercentage}}
              {{#if this.period.showOccupancyChange}}
                <GeneralPercentageChangeValue
                  @changeValue={{this.period.occupancyChange}}
                />
              {{/if}}
            {{else}}
              {{t "general.missing"}}
            {{/if}}
          </p>
        {{/if}}
      </div>
      <div class="{{styles.otb-bar}} flex h-[36px]">
        {{#if this.showOtbBar}}
          <div
            class="{{styles.otb-bar}} self-end {{this.otbBarColor}}"
            data-test-id="day-cell__otb-bar"
            style={{this.otbBarPercentage}}
          />
        {{else if @column.isLoading}}
          <div class="{{styles.otb-bar}} self-end" />
        {{/if}}
      </div>
      {{#if (and this.showTooltip this.period)}}
        <HotelChannelPricingTooltipsDayCellRoomtypeAvailabilityTooltip
          @date={{@column.date}}
          {{! @glint-expect-error: Incorrect type }}
          @period={{this.period}}
        />
      {{/if}}
    </div>
  </template>
}
